import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-notes-medical',
    title: '1. EHR/EMR Integration & Interoperability',
    desc: 'Connect disparate health systems seamlessly using HL7 and FHIR standards to enable secure, real-time patient data exchange across networks.',
  },
  {
    icon: 'fa-solid fa-dna',
    title: '2. Bioinformatics & Genomics Data Pipelines',
    desc: 'Build high-throughput data pipelines for genomic sequencing, variant analysis, and computational biology workloads at enterprise scale.',
  },
  {
    icon: 'fa-solid fa-stethoscope',
    title: '3. AI-Powered Medical Imaging & Diagnostics',
    desc: 'Deploy deep learning models on DICOM images to assist clinicians with early defect detection, tumor segmentation, and radiomic analysis.',
  },
  {
    icon: 'fa-solid fa-user-doctor',
    title: '4. Telemedicine & Virtual Care Platforms',
    desc: 'Engineer secure, webRTC-powered telehealth portals featuring remote patient monitoring (RPM), automated triage, and integrated e-prescriptions.',
  },
  {
    icon: 'fa-solid fa-vials',
    title: '5. Clinical Trial Management Systems (CTMS)',
    desc: 'Streamline patient recruitment, trial monitoring, electronic data capture (EDC), and real-time safety reporting for pharmaceutical pipelines.',
  },
  {
    icon: 'fa-solid fa-shield-heart',
    title: '6. HIPAA & GDPR Health Data Security',
    desc: 'Enforce zero-trust security postures, end-to-end encryption, automated audit logging, and strict BAA compliance across cloud health environments.',
  },
  {
    icon: 'fa-solid fa-pills',
    title: '7. AI Drug Discovery & Target Identification',
    desc: 'Accelerate pre-clinical research using machine learning algorithms to predict molecular interactions, toxicity, and target binding affinity.',
  },
  {
    icon: 'fa-solid fa-laptop-medical',
    title: '8. Digital Therapeutics & SaMD Solutions',
    desc: 'Design and certify Software as a Medical Device (SaMD) compliant with FDA 21 CFR Part 11 and ISO 13485 regulations for patient care.',
  },
  {
    icon: 'fa-solid fa-chart-line-up',
    title: '9. Healthcare Predictive Analytics',
    desc: 'Leverage machine learning to forecast hospital readmissions, optimize ICU bed allocation, and predict patient risk trajectories.',
  },
  {
    icon: 'fa-solid fa-heart-pulse',
    title: '10. IoT Medical Device & Wearable Ingestion',
    desc: 'Ingest real-time streaming telemetry from continuous glucose monitors, ECG wearables, and smart medical sensors securely to the cloud.',
  },
];

const whyChooseUs = [
  'Strict adherence to HIPAA, GDPR, HITECH, and FDA 21 CFR Part 11 standards',
  'Deep expertise in HL7 v2/v3, FHIR, and DICOM healthcare standards',
  'Scalable cloud infrastructure optimized for multi-omics data storage',
  'Proven track record in deploying certified Software as a Medical Device (SaMD)',
  'Zero-trust security architecture protecting Protected Health Information (PHI)',
  'Custom AI diagnostic models integrated directly into clinical workflows',
  'Seamless interoperability with Epic, Cerner, Allscripts, and Athenahealth',
  'End-to-end clinical trial digitization and EDC systems',
  'Real-time streaming pipelines for remote patient monitoring (RPM)',
  'Dedicated healthcare IT architects and domain-certified engineers',
];

const processSteps = [
  { step: '01', title: 'Clinical Assessment & Compliance Mapping', desc: 'Audit current systems, data flows, and security policies against HIPAA, FDA, and international health regulations.' },
  { step: '02', title: 'Architecture & Interoperability Design', desc: 'Establish FHIR/HL7 interfaces, secure cloud landing zones, and encrypted database schemes.' },
  { step: '03', title: 'Secure Development & AI Engineering', desc: 'Develop compliant software, fine-tune medical AI algorithms, and construct bio-data processing pipelines.' },
  { step: '04', title: 'Validation & Regulatory Testing', desc: 'Perform rigorous software validation (IQ/OQ/PQ), vulnerability assessments, and clinical accuracy checks.' },
  { step: '05', title: 'EHR Integration & Deployment', desc: 'Deploy within production hospital/cloud environments with smooth EHR integration and single sign-on.' },
  { step: '06', title: 'Continuous Auditing & Monitoring', desc: 'Maintain 24/7 audit readiness, manage continuous HIPAA compliance, and monitor algorithm drift.' },
];

const techStack = [
  { category: 'Interoperability & Standards', items: ['HL7 FHIR', 'DICOM', 'SNOMED CT', 'LOINC', 'RxNorm'] },
  { category: 'Health Cloud & Security', items: ['AWS HealthLake', 'Azure Health Data Services', 'Google Cloud Healthcare', 'HIPAA BAA'] },
  { category: 'Biotech & AI Tools', items: ['Biopython', 'Nextflow', 'PyTorch', 'TensorFlow', 'OpenCV'] },
  { category: 'EHR & Platform Integrations', items: ['Epic App Orchard', 'Cerner Ignite', 'SMART on FHIR', 'WebRTC'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const HealthcareAndBiotech = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Life Sciences & Health
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Healthcare & <span className="gradient-text-cyan">Biotech Solutions</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we build compliant, intelligent digital health systems. From AI-assisted medical imaging and genomics data pipelines to secure FHIR integrations and digital therapeutics, we accelerate clinical outcomes.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Advance Your Healthtech Strategy &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Healthcare Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Healthcare & Biotech Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Health Engineering Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Innovate Safely in Healthcare with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Transform clinical operations, accelerate therapeutic discovery, and ensure uncompromised data protection across your digital health portfolio.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a Healthtech Specialist &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HealthcareAndBiotech;